import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, flatMap, map, tap} from 'rxjs/operators';
import * as UrlAssembler from 'url-assembler';
import {environment} from '../../../environments/environment';
import {CachingInterceptor} from '../../caching/services/caching-interceptor';
import {stripHtmlTags} from '../../shared/helper/util';
import {Attribute} from '../models/attribute';
import {Product, ProductAttributeValue} from '../models/product';
import {ProductsAttributesApiService} from './products-attributes-api.service';
import {productsErrorSelector} from './products-error-selector';
import {ProductQuery} from "../models/product-query";

const ProductsResources = {
    products: '/V1/extendedProducts',
    productMedia: '/media/catalog/product'
};

@Injectable()
export class ProductsApiService {
    constructor(private http: HttpClient, private productsAttributesService: ProductsAttributesApiService) {
    }

    protected determineProductsResourceUrl(): string {
        return environment.rest_backend + ProductsResources.products;
    }

    protected determineProductMediaResourceUrlPrefix(): string {
        return environment.backend + ProductsResources.productMedia;
    }

    protected determineProductsResourceUrlWithQuery(query: ProductQuery): string {
        return UrlAssembler(this.determineProductsResourceUrl()).query({
            ...this.constructPageSizeQueryParameter(query),
            ...this.constructCurrentPageQueryParameter(query),
            ...this.constructSortOrderQueryParameters(query),
            ...this.constructFilterGroupsParameters(query),
            ...this.constructFieldsParameter()
        }).toString();
    }

    public getProducts(query: ProductQuery): Observable<ProductsResult> {
        return this.productsAttributesService.getAttributesOfSet('Decor').pipe(
            flatMap(attributes => this.getProductsWithAttributes(query, attributes))
        );
    }

    private getProductsWithAttributes(query: ProductQuery, attributes: Attribute[]): Observable<ProductsResult> {
        const url = this.determineProductsResourceUrlWithQuery(query);

        console.log('load products from: ', url);

        return this.http.get<ProductsEntity>(url, {
            headers: CachingInterceptor.createCacheHeader()
        }).pipe(
            tap(response => console.log('loaded products entity: ', response)),
            map(productsEntity => this.convertToProductsResult(productsEntity, attributes)),
            catchError(productsErrorSelector)
        );
    }

    private constructPageSizeQueryParameter(query: ProductQuery) {
        return {
            'searchCriteria[page_size]': query.pageSize
        };
    }

    private constructCurrentPageQueryParameter(query: ProductQuery) {
        return {
            'searchCriteria[current_page]': query.page
        };
    }

    private constructSortOrderQueryParameters(query: ProductQuery) {
        const params = {};
        const sortOrders = query.sortOrders;

        for (let i = 0; i < sortOrders.length; i++) {
            const sortOrder = sortOrders[i];

            params[`searchCriteria[sortOrders][${i}][field]`] = sortOrder.field;
            params[`searchCriteria[sortOrders][${i}][direction]`] = sortOrder.direction;
        }

        return params;
    }

    private constructFilterGroupsParameters(query: ProductQuery) {
        const params = {};
        const filterGroups = query.filterGroups;

        for (let i = 0; i < filterGroups.length; i++) {
            const filterGroup = filterGroups[i];
            const filters = filterGroup.filters;

            for (let j = 0; j < filters.length; j++) {
                const filter = filters[j];

                params[`searchCriteria[filter_groups][${i}][filters][${j}][field]`] = filter.field;
                params[`searchCriteria[filter_groups][${i}][filters][${j}][value]`] = filter.value;
                params[`searchCriteria[filter_groups][${i}][filters][${j}][condition_type]`] = filter.conditionType;
            }
        }

        return params;
    }

    private constructFieldsParameter() {
        return {
            fields: 'items[id,sku,name,custom_attributes,price,media_gallery_entries[file]],search_criteria,total_count'
        };
    }

    private convertToProductsResult(productsEntity: ProductsEntity, attributes: Attribute[]): ProductsResult {
        let products;

        const criteria = productsEntity.search_criteria;
        if ((criteria.current_page - 1) * criteria.page_size > productsEntity.total_count) {
            // Actually there are no more products. I don't know why, but somehow we've to handle this scenario manually.
            // Magneto always returns the last existing page if the current page doesn't exist anymore.
            products = [];
        } else if (productsEntity.items == null) {
            products = [];
        } else {
            products = productsEntity.items.map(item => this.convertToProduct(item, attributes));
        }

        return {products: products, totalCount: productsEntity.total_count};
    }

    private convertToProduct(productEntity: ProductEntity, attributes: Attribute[]): Product {
        return {
            id: productEntity.id,
            name: this.correctText(productEntity.name),
            sku: productEntity.sku,
            price: productEntity.price,
            mediaGalleryEntries: productEntity.media_gallery_entries != null ? productEntity.media_gallery_entries.map((entry) => entry.file) : [],
            description: null,
            shortDescription: null,
            metaTitle: null,
            metaKeyword: null,
            metaDescription: null,
            baseImageUrl: null,
            smallImageUrl: null,
            thumbnailUrl: null,
            decor: null,
            dishwasherSafe: null,
            microwaveSafe: null,
            measurementsMetric: null,
            measurementsImperial: null,
            new: null,
            ...this.determineCustomAttributeValues(productEntity, attributes)
        };
    }

    private determineCustomAttributeValues(productEntity: ProductEntity, attributes: Attribute[]) {
        const customAttributes = {};

        for (const customAttribute of productEntity.custom_attributes) {
            switch (customAttribute.attribute_code) {
                case 'description':
                    customAttributes['description'] = this.correctText(customAttribute.value);
                    break;
                case 'short_description':
                    customAttributes['shortDescription'] = this.correctText(customAttribute.value);
                    break;
                case 'meta_title':
                    if (customAttribute.value == null) {
                        customAttribute.value = productEntity.name;
                    }
                    customAttributes['metaTitle'] = customAttribute.value;
                    break;
                case 'meta_keyword':
                    customAttributes['metaKeyword'] = customAttribute.value;
                    break;
                case 'meta_description':
                    if (customAttribute.value == null) {
                        customAttribute.value = '';
                    }
                    customAttributes['metaDescription'] = customAttribute.value;
                    break;
                case 'image':
                    customAttributes['baseImageUrl'] = this.getImageUrl(customAttribute.value);
                    break;
                case 'small_image':
                    customAttributes['smallImageUrl'] = this.getImageUrl(customAttribute.value);
                    break;
                case 'thumbnail':
                    customAttributes['thumbnailUrl'] = this.getImageUrl(customAttribute.value);
                    break;
                case 'decor':
                    if (customAttribute.value == null) {
                        customAttribute.value = '';
                    }
                    customAttributes['decor'] = this.getProductAttributeValue(customAttribute, attributes);
                    break;
                case 'dishwasher_safe':
                    customAttributes['dishwasherSafe'] = this.getProductAttributeValue(customAttribute, attributes);
                    break;
                case 'microwave_safe':
                    customAttributes['microwaveSafe'] = this.getProductAttributeValue(customAttribute, attributes);
                    break;
                case 'measurements_metric':
                    customAttributes['measurementsMetric'] = this.getProductAttributeValue(customAttribute, attributes);
                    break;
                case 'measurements_imperial':
                    customAttributes['measurementsImperial'] = this.getProductAttributeValue(customAttribute, attributes);
                    break;
                case 'new_product':
                    customAttributes['new'] = this.getProductAttributeValue(customAttribute, attributes);
                    break;
            }
        }

        return customAttributes;
    }

    /**
     * Helper method decoding the given text and stripping html tags.
     *
     * @param text
     */
    private correctText(text: string): string {
        if (text == null) {
            return null;
        }
        const txt = document.createElement('textarea');
        txt.innerHTML = text;
        return stripHtmlTags(txt.value);
    }

    private getProductAttributeValue(attributeEntity: CustomAttributeEntity, attributes: Attribute[]): ProductAttributeValue {
        const attribute = attributes.filter(a => a.code === attributeEntity.attribute_code)[0];
        const options = attribute.options.filter(o => o.value === attributeEntity.value);

        if (options.length === 0) {
            return {
                code: attribute.code,
                value: attributeEntity.value,
                label: this.correctText(attributeEntity.value)
            };
        }

        return {
            code: attribute.code,
            value: options[0].value,
            label: options[0].label
        };
    }

    private getImageUrl(relativePath: string) {
        return this.determineProductMediaResourceUrlPrefix() + relativePath;
    }
}

export interface ProductsResult {
    products: Product[];
    totalCount: number;
}

interface ProductsEntity {
    items: ProductEntity[];
    search_criteria: SearchCriteriaEntity;
    total_count: number;
}

interface ProductEntity {
    id: number;
    sku: string;
    price: number;
    name: string;
    media_gallery_entries: {
        [file: string]: string;
    }[];
    custom_attributes: CustomAttributeEntity[];
}

interface CustomAttributeEntity {
    attribute_code: string;
    value: string;
}

interface SearchCriteriaEntity {
    current_page: number;
    page_size: number;
    // it also includes sort_orders and filter_groups
    // I think they're not needed at this point.
}
